import { inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, switchMap, tap, map, finalize, of, shareReplay, ReplaySubject, timeout, catchError } from 'rxjs';
import { LoginResponse, UserWithRole, RegisterRequest, ResourceUsage } from '../models/user.model';
import { MeResponse, UserRole, UserType } from '../models/role.model';
import { CompanyProfile } from '../models/company-profile.model';
import { STORAGE_KEYS } from '../constants/storage.const';
import { API_ENDPOINTS } from '../constants/api.const';
import { environment } from '@environments/environment';
import { MaidService } from '@features/maid/shared/services/maid.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly maidService = inject(MaidService);

    // Signal for current user data (with role)
    readonly currentUser = signal<UserWithRole | null>(null);

    // Signal for company profile data (only for agency user_type)
    readonly companyProfile = signal<CompanyProfile | null>(null);

    // Signal for authentication status (derived from token existence in localStorage)
    readonly isAuthenticated = signal<boolean>(false);

    // Signal for loading user data (during fetch)
    readonly isLoadingUser = signal<boolean>(false);

    // Signal for logout in progress (for full-page loading overlay)
    readonly isLoggingOut = signal<boolean>(false);

    // Track in-flight request to prevent duplicate calls
    private userDataRequest$: Observable<MeResponse> | null = null;

    // ReplaySubject for auth readiness - emits once after auth state is resolved
    private readonly authReadySubject = new ReplaySubject<boolean>(1);
    readonly authReady$ = this.authReadySubject.asObservable();

    // Computed signal for current role
    readonly currentRole = computed<UserRole | null>(() => {
        return this.currentUser()?.role ?? null;
    });

    // Computed signal for current user type (for routing/layout)
    readonly currentUserType = computed<UserType | null>(() => {
        return this.currentUser()?.user_type ?? null;
    });

    // Computed signal for resource usage (for subscription validation)
    readonly resourceUsage = computed<ResourceUsage | null>(() => {
        return this.currentUser()?.resource_usage ?? null;
    });

    constructor() {
        // Restore auth state from localStorage on app initialization
        this.restoreAuthState();
    }

    /**
     * Get token from localStorage
     */
    getToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    }

    /**
     * Check if token exists in localStorage
     */
    hasToken(): boolean {
        return !!this.getToken();
    }

    /**
     * Store token in localStorage
     */
    private storeToken(token: string): void {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    }

    /**
     * Remove token from localStorage
     */
    private removeToken(): void {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    }

    private restoreAuthState(): void {
        // If no localStorage token, check for cookie token (set by WordPress login)
        if (!this.hasToken()) {
            const cookieToken = this.getTokenFromCookie();
            if (cookieToken) {
                this.storeToken(cookieToken);
                this.clearTokenCookie();
            }
        }

        // No token at all - not authenticated
        if (!this.hasToken()) {
            this.isAuthenticated.set(false);
            this.authReadySubject.next(false);
            return;
        }

        // Token exists - validate via /me
        this.fetchUserData().subscribe({
            next: () => {
                this.isAuthenticated.set(true);
                this.authReadySubject.next(true);
            },
            error: () => {
                this.logout();
                this.isAuthenticated.set(false);
                this.authReadySubject.next(false);
            }
        });
    }

    /**
     * Read token from cookie (set by WordPress after login)
     */
    private getTokenFromCookie(): string | null {
        const match = document.cookie.match(/(?:^|;\s*)token=([^;]*)/);
        return match ? decodeURIComponent(match[1]) : null;
    }

    /**
     * Clear the token cookie after reading it into localStorage
     */
    private clearTokenCookie(): void {
        document.cookie = 'token=; Path=/; Domain=.intellimaid.com.sg; Max-Age=0';
    }

    /**
     * Force refresh user data from /api/me (bypasses cache)
     * Use after mutations that change user/company profile data
     * Note: Does NOT clear currentUser to avoid triggering type-based guards/effects
     */
    refreshUserData(): Observable<MeResponse> {
        // Clear in-flight request cache and make fresh API call
        this.userDataRequest$ = null;
        return this.fetchUserDataInternal();
    }

    /**
     * Fetch user data and role from /api/me
     * Prevents duplicate requests when called multiple times
     */
    fetchUserData(): Observable<MeResponse> {
        // Already loaded? Return cached data
        const cachedUser = this.currentUser();
        if (cachedUser) {
            return of(this.buildCachedResponse(cachedUser));
        }

        // Request in flight? Return that same observable
        if (this.userDataRequest$) {
            return this.userDataRequest$;
        }

        return this.fetchUserDataInternal();
    }

    /**
     * Internal method that performs the actual HTTP fetch
     */
    private fetchUserDataInternal(): Observable<MeResponse> {
        this.isLoadingUser.set(true);
        const url = `${environment.API_URL}${API_ENDPOINTS.auth.me}`;

        this.userDataRequest$ = this.http.get<MeResponse>(url).pipe(
            tap((response) => {
                const roleName = response.data.user.roles[0]?.name as UserRole;
                const userWithRole: UserWithRole = {
                    ...response.data.user,
                    role: roleName,
                    permissions: response.data.permissions,
                    resource_usage: response.data.resource_usage ?? null,
                };
                this.currentUser.set(userWithRole);
                this.companyProfile.set(response.data.company_profile ?? null);

                // Load maid profile in parallel for maid user type
                if (userWithRole.user_type === 'maid') {
                    this.maidService.loadMaidProfile().subscribe({
                        error: () => {
                            // Silently handle error - maid profile is optional
                        }
                    });
                }
            }),
            shareReplay(1),
            finalize(() => {
                this.isLoadingUser.set(false);
                this.userDataRequest$ = null;
            })
        );

        return this.userDataRequest$;
    }

    /**
     * Build response from cached user data
     */
    private buildCachedResponse(user: UserWithRole): MeResponse {
        return {
            status: 'success',
            message: 'OK',
            data: {
                user: {
                    id: user.id,
                    agency_id: user.agency_id,
                    roles_id: user.roles_id,
                    user_type: user.user_type,
                    google_id: user.google_id,
                    name: user.name,
                    email: user.email,
                    avatar: user.avatar,
                    email_verified_at: user.email_verified_at,
                    phone_number: user.phone_number,
                    nric: user.nric,
                    office_address: user.office_address,
                    is_active: user.is_active,
                    created_at: user.created_at,
                    updated_at: user.updated_at,
                    roles: [{
                        id: user.roles_id,
                        agency_id: user.agency_id,
                        name: user.role,
                        description: null,
                        guard_name: 'api',
                        user_type: user.user_type,
                        created_at: user.created_at,
                        updated_at: user.updated_at,
                        permissions: [],
                    }],
                },
                roles: [user.role],
                permissions: user.permissions,
            },
        };
    }

    /**
     * Redirect to appropriate dashboard based on user_type
     * user_type determines the domain/layout for routing
     * For employer type, redirects to WordPress instead of Angular route
     * @returns true if redirecting externally (WordPress), false for internal navigation
     */
    redirectBasedOnRole(): boolean {
        const userType = this.currentUserType();

        // user_type determines domain/layout
        switch (userType) {
            case 'owner':
                // All owner-type users go to super-admin features
                this.router.navigate(['/dashboard']);
                return false;
            case 'agency':
                this.router.navigate(['/agency/dashboard']);
                return false;
            case 'maid':
                this.router.navigate(['/maid/dashboard']);
                return false;
            case 'employer':
                // Redirect to employer dashboard
                this.router.navigate(['/employer/dashboard']);
                return false;
            default:
                // Fallback - shouldn't happen with valid API response
                this.router.navigate(['/dashboard']);
                return false;
        }
    }

    /**
     * Login with email and password
     * Returns Observable with isExternalRedirect flag (true = redirecting to WordPress)
     */
    login(email: string, password: string): Observable<{ isExternalRedirect: boolean }> {
        const url = `${environment.API_URL}${API_ENDPOINTS.auth.login}`;

        return this.http.post<LoginResponse>(url, { email, password }).pipe(
            switchMap((response) => {
                const { token } = response.data;

                // Store only token in localStorage
                this.storeToken(token);
                this.isAuthenticated.set(true);

                // Fetch user data including role
                return this.fetchUserData().pipe(
                    map(() => {
                        // Emit auth ready after successful login
                        this.authReadySubject.next(true);

                        // Redirect based on role, return whether it's external
                        const isExternalRedirect = this.redirectBasedOnRole();
                        return { isExternalRedirect };
                    })
                );
            })
        );
    }

    /**
     * Request password reset email
     * Returns Observable that completes when request finishes
     */
    forgotPassword(email: string): Observable<void> {
        const url = `${environment.API_URL}${API_ENDPOINTS.auth.forgotPassword}`;

        return this.http.post<void>(url, { email });
    }

    /**
     * Register new employer account
     * Returns Observable that completes when request finishes
     */
    register(data: RegisterRequest): Observable<void> {
        const url = `${environment.API_URL}${API_ENDPOINTS.auth.register}`;

        return this.http.post<void>(url, data);
    }

    /**
     * Reset password with token
     * Returns Observable that completes when request finishes
     */
    resetPassword(token: string, email: string, password: string, passwordConfirmation: string): Observable<void> {
        const url = `${environment.API_URL}${API_ENDPOINTS.auth.resetPassword}`;

        return this.http.post<void>(url, { token, email, password, password_confirmation: passwordConfirmation });
    }

    /**
     * Change password for authenticated user
     * Returns Observable that completes when request finishes
     */
    changePassword(password: string, passwordConfirmation: string): Observable<void> {
        const url = `${environment.API_URL}${API_ENDPOINTS.auth.changePassword}`;

        return this.http.post<void>(url, { password, password_confirmation: passwordConfirmation });
    }

    /**
     * Logout user
     * Calls logout API and clears local state
     * Uses 5-second timeout to ensure logout always completes
     */
    logout(): void {
        // Show loading overlay immediately
        this.isLoggingOut.set(true);

        const url = `${environment.API_URL}${API_ENDPOINTS.auth.logout}`;

        // Call logout API with timeout to ensure it always completes
        this.http.post<void>(url, {}).pipe(
            timeout(5000), // 5 second timeout
            catchError(() => of(undefined)), // Ignore errors, proceed with logout
            finalize(() => {
                // Always clear local state, even if API fails or times out
                this.removeToken();
                this.currentUser.set(null);
                this.companyProfile.set(null);
                this.maidService.maidProfile.set(null);
                this.isAuthenticated.set(false);
                this.isLoggingOut.set(false);

                // Emit auth ready false after logout
                this.authReadySubject.next(false);

                // All users redirect to auth login page
                this.router.navigate(['/auth']);
            })
        ).subscribe();
    }
}
